// Shared types, constants, and helpers for the agency (legal service provider) forms.

export interface ServiceProviderFormState {
  organizationName: string;
  organizationTypes: string[];
  organizationTypeOther: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  registrantName: string;
  registrantEmail: string;
  publicServicesEmail: string;
  authorizerName: string;
  authorizerTitle: string;
  authorizerEmail: string;
  serviceCostTypes: string[];
  eligibilityCriteria: string;
  serviceJurisdiction: string;
  servicesProvided: string;
}

export interface ServiceProviderValidationErrors {
  organizationName?: string;
  organizationTypes?: string;
  organizationTypeOther?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  registrantName?: string;
  registrantEmail?: string;
  publicServicesEmail?: string;
  authorizerName?: string;
  authorizerTitle?: string;
  authorizerEmail?: string;
  serviceCostTypes?: string;
  servicesProvided?: string;
  eligibilityCriteria?: string;
  serviceJurisdiction?: string;
}

export const ORG_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'bar_association', label: 'Bar Association' },
  { value: 'government', label: 'Government' },
  { value: 'nonprofit', label: 'Other Non-profit' },
  { value: 'other', label: 'Other' },
];

export const COST_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'free', label: 'Free Services' },
  { value: 'discounted', label: 'Discounted Services' },
  { value: 'full_price', label: 'Full Price Services' },
];

export const ORG_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ORG_TYPE_OPTIONS.map((o) => [o.value, o.label])
);
export const COST_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  COST_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

export const emptyServiceProviderForm = (): ServiceProviderFormState => ({
  organizationName: '',
  organizationTypes: [],
  organizationTypeOther: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  registrantName: '',
  registrantEmail: '',
  publicServicesEmail: '',
  authorizerName: '',
  authorizerTitle: '',
  authorizerEmail: '',
  serviceCostTypes: [],
  eligibilityCriteria: '',
  serviceJurisdiction: '',
  servicesProvided: ''
});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validatePhoneNumber(phone: string): boolean {
  // Remove all non-digit characters for validation
  const cleaned = phone.replace(/\D/g, '');
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(cleaned);
}

export function validateZipCode(zip: string): boolean {
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zip);
}

// Format phone number as (xxx) xxx-xxxx
export function formatPhoneNumber(phoneNumber: string): string {
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phoneNumber;
}

export function validateServiceProviderForm(
  form: ServiceProviderFormState,
  options: { requireRegistrantEmail?: boolean } = {}
): ServiceProviderValidationErrors {
  const errors: ServiceProviderValidationErrors = {};
  const { requireRegistrantEmail = true } = options;

  if (!form.organizationName.trim()) errors.organizationName = 'Organization name is required';
  if (form.organizationTypes.length === 0) errors.organizationTypes = 'Select at least one organization type';
  if (form.organizationTypes.includes('other') && !form.organizationTypeOther.trim()) {
    errors.organizationTypeOther = 'Please specify the organization type';
  }
  if (!form.addressLine1.trim()) errors.addressLine1 = 'Address is required';
  if (!form.city.trim()) errors.city = 'City is required';
  if (!form.state.trim()) errors.state = 'State is required';

  if (!form.zipCode.trim()) {
    errors.zipCode = 'Zip code is required';
  } else if (!validateZipCode(form.zipCode)) {
    errors.zipCode = 'Please enter a valid zip code';
  }

  if (!form.phone.trim()) {
    errors.phone = 'Phone is required';
  } else if (!validatePhoneNumber(form.phone)) {
    errors.phone = 'Please enter a valid phone number';
  }

  if (!form.registrantName.trim()) errors.registrantName = 'Your name is required';

  if (requireRegistrantEmail) {
    if (!form.registrantEmail.trim()) {
      errors.registrantEmail = 'Registration email is required';
    } else if (!emailRegex.test(form.registrantEmail)) {
      errors.registrantEmail = 'Please enter a valid email address';
    }
  }
  if (form.publicServicesEmail.trim() && !emailRegex.test(form.publicServicesEmail)) {
    errors.publicServicesEmail = 'Please enter a valid email address';
  }

  // Authorizer fields are optional in the UI (section may be hidden); fallbacks are applied at submit.
  if (form.authorizerName.trim() && !form.authorizerTitle.trim()) {
    errors.authorizerTitle = 'Authorizer title is required when authorizer name is provided';
  }
  if (form.authorizerEmail.trim() && !emailRegex.test(form.authorizerEmail)) {
    errors.authorizerEmail = 'Please enter a valid email address';
  }

  if (form.serviceCostTypes.length === 0) errors.serviceCostTypes = 'Select at least one option';

  if (!form.eligibilityCriteria.trim()) errors.eligibilityCriteria = 'Eligibility criteria is required';
  if (!form.serviceJurisdiction.trim()) errors.serviceJurisdiction = 'Service jurisdiction is required';
  if (!form.servicesProvided.trim()) errors.servicesProvided = 'Services provided is required';

  return errors;
}

// Scroll to (and focus) the first field with a validation error, in visual form order.
export function scrollServiceProviderToFirstError(errors: ServiceProviderValidationErrors): void {
  const byName = (name: string): { scroll: HTMLElement | null; focus: HTMLElement | null } => {
    const input = document.querySelector(`[name="${name}"]`) as HTMLElement | null;
    return {
      scroll: (input?.closest('.MuiTextField-root') as HTMLElement) || input,
      focus: input,
    };
  };
  const byId = (id: string): { scroll: HTMLElement | null; focus: HTMLElement | null } => {
    const el = document.getElementById(id);
    return { scroll: el, focus: null };
  };

  // Order must match the visual order of fields in ServiceProviderFormFields.
  const order: { key: keyof ServiceProviderValidationErrors; find: () => { scroll: HTMLElement | null; focus: HTMLElement | null } }[] = [
    { key: 'organizationName', find: () => byName('organizationName') },
    { key: 'organizationTypes', find: () => byId('sp-anchor-organizationTypes') },
    { key: 'organizationTypeOther', find: () => byName('organizationTypeOther') },
    { key: 'addressLine1', find: () => byName('addressLine1') },
    { key: 'city', find: () => byName('city') },
    { key: 'state', find: () => byId('sp-anchor-state') },
    { key: 'zipCode', find: () => byName('zipCode') },
    { key: 'phone', find: () => byName('phone') },
    { key: 'registrantName', find: () => byName('registrantName') },
    { key: 'registrantEmail', find: () => byName('registrantEmail') },
    { key: 'publicServicesEmail', find: () => byName('publicServicesEmail') },
    { key: 'serviceCostTypes', find: () => byId('sp-anchor-serviceCostTypes') },
    { key: 'eligibilityCriteria', find: () => byName('eligibilityCriteria') },
    { key: 'serviceJurisdiction', find: () => byName('serviceJurisdiction') },
    { key: 'servicesProvided', find: () => byName('servicesProvided') },
  ];

  // Defer so the DOM has rendered the error states first.
  setTimeout(() => {
    for (const { key, find } of order) {
      if (!errors[key]) continue;
      const { scroll, focus } = find();
      if (!scroll) continue;
      const yOffset = -100; // account for fixed headers
      const y = scroll.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      if (focus instanceof HTMLInputElement || focus instanceof HTMLTextAreaElement) {
        setTimeout(() => focus.focus(), 300);
      }
      break;
    }
  }, 100);
}

// Map an API provider record into editable form state
export function providerToFormState(p: any): ServiceProviderFormState {
  return {
    organizationName: p?.organizationName || '',
    organizationTypes: Array.isArray(p?.organizationTypes) ? p.organizationTypes : [],
    organizationTypeOther: p?.organizationTypeOther || '',
    addressLine1: p?.addressLine1 || '',
    addressLine2: p?.addressLine2 || '',
    city: p?.city || '',
    state: p?.state || '',
    zipCode: p?.zipCode || '',
    phone: p?.phone || '',
    registrantName: p?.registrantName || '',
    registrantEmail: p?.registrantEmail || '',
    publicServicesEmail: p?.publicServicesEmail || '',
    authorizerName: p?.authorizerName || '',
    authorizerTitle: p?.authorizerTitle || '',
    authorizerEmail: p?.authorizerEmail || '',
    serviceCostTypes: Array.isArray(p?.serviceCostTypes) ? p.serviceCostTypes : [],
    eligibilityCriteria: p?.eligibilityCriteria || '',
    serviceJurisdiction: p?.serviceJurisdiction || '',
    servicesProvided: p?.servicesProvided || '',
  };
}

// Build the API payload from form state
export function buildServiceProviderPayload(form: ServiceProviderFormState) {
  return {
    organizationName: form.organizationName.trim(),
    organizationTypes: form.organizationTypes,
    organizationTypeOther: form.organizationTypes.includes('other') ? form.organizationTypeOther.trim() : null,
    addressLine1: form.addressLine1.trim(),
    addressLine2: form.addressLine2.trim() || undefined,
    city: form.city.trim(),
    state: form.state,
    zipCode: form.zipCode.trim(),
    phone: form.phone.trim(),
    registrantName: form.registrantName.trim(),
    registrantEmail: form.registrantEmail.trim(),
    publicServicesEmail: form.publicServicesEmail.trim() || undefined,
    authorizerName: form.authorizerName.trim() || form.registrantName.trim(),
    authorizerTitle: form.authorizerTitle.trim() || 'Registrant',
    authorizerEmail: form.authorizerEmail.trim() || form.registrantEmail.trim(),
    serviceCostTypes: form.serviceCostTypes,
    eligibilityCriteria: form.eligibilityCriteria.trim(),
    serviceJurisdiction: form.serviceJurisdiction.trim(),
    servicesProvided: form.servicesProvided.trim(),
  };
}
